import { useCallback } from 'react';
import { faCloudArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  CircularProgressIndicator,
  CopyableText,
  Stack,
  Text,
} from '@sixui/ui';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiGetLiveStreamsItemRecordingResponseBody,
  IApiGetLiveStreamsItemResponseBody,
} from '~/lib/api/modules/LiveStreams';
import { Duration } from '~/components/atoms/Duration';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { HlsVideoPlayer } from '~/components/molecules/HlsVideoPlayer';
import { IAny } from '~/helpers/types';
import { useAppRegion } from '~/hooks/useAppRegion';
import { useRefreshLiveStreamsMutation } from '~/lib/api/modules/LiveStreams/hooks/useRefreshLiveStreamsMutation';
import { useRequestDownloadLiveStreamsMutation } from '~/lib/api/modules/LiveStreams/hooks/useRequestDownloadLiveStreamsMutation';
import { appRegionsSettings } from '~/lib/appSettings';

const RECORDING_WIDTH = 240;
const RECORDING_HEIGHT = Math.round(RECORDING_WIDTH / 1.8);

export type ILiveStreamRecordingListItemProps = IContainerProps & {
  liveStream: IApiGetLiveStreamsItemResponseBody;
  recording: IApiGetLiveStreamsItemRecordingResponseBody;
};

const styles = stylex.create({
  infoList: {
    rowGap: spacingTokens.padding$2,
    paddingBottom: 0,
  },
});

export const LiveStreamRecordingListItem: React.FC<
  ILiveStreamRecordingListItemProps
> = (props) => {
  const { sx, liveStream, recording } = props;
  const { t, i18n } = useTranslation();
  const requestDownloadLiveStreamsMutation =
    useRequestDownloadLiveStreamsMutation();
  const refreshLiveStreamsMutation = useRefreshLiveStreamsMutation();
  const appRegion = useAppRegion();

  const playbackUrl =
    recording.baseUrl && recording.root && recording.playlist
      ? `${recording.baseUrl}/${recording.root}/${recording.playlist}`
      : undefined;
  const downloadUrl =
    recording.baseUrl && recording.conversion?.fileKey
      ? `${recording.baseUrl}/${recording.conversion.fileKey}`
      : undefined;
  const prepareDownloadProgress =
    recording.conversion?.status === 'PROGRESSING' &&
    recording.conversion.completion !== undefined
      ? recording.conversion.completion / 100
      : undefined;
  const prepareDownloadFormattedProgress =
    prepareDownloadProgress !== undefined
      ? `${Math.round(prepareDownloadProgress * 100)}%`
      : undefined;

  const handleRequestDownload = useCallback(
    (): Promise<IAny> | undefined =>
      recording.root
        ? requestDownloadLiveStreamsMutation
            .mutateAsync({
              appId: liveStream.appId,
              liveStreamId: liveStream._id,
              params: {
                recordingRoot: recording.root,
              },
            })
            .then(() =>
              refreshLiveStreamsMutation.mutateAsync({
                appId: liveStream.appId,
                liveStreamId: liveStream._id,
              }),
            )
        : undefined,
    [
      requestDownloadLiveStreamsMutation,
      refreshLiveStreamsMutation,
      liveStream._id,
      liveStream.appId,
      recording.root,
    ],
  );

  const iframeCode = recording.root
    ? `<iframe src="${appRegionsSettings[appRegion].apiUrl}/liveStream/${liveStream.appId},${liveStream._id}/recording?recordingId=${encodeURIComponent(recording.root)}" frameborder="0" allowfullscreen="true" style="overflow:hidden;height:56vw;width:100%"></iframe>`
    : undefined;

  return (
    <Stack gap={2}>
      <Stack horizontal sx={sx} gap={2}>
        {playbackUrl && (
          <HlsVideoPlayer
            src={playbackUrl}
            width={RECORDING_WIDTH}
            height={RECORDING_HEIGHT}
            preload="auto"
            controls
            playButton
          />
        )}

        <ContentBlock.InfoList sx={styles.infoList}>
          {recording.start && (
            <ContentBlock.InfoListItem
              label={t('LiveStreamRecordingListItem.start')}
            >
              {DateTime.fromISO(recording.start).toLocaleString(
                DateTime.DATETIME_FULL,
                { locale: i18n.language },
              )}
            </ContentBlock.InfoListItem>
          )}

          <ContentBlock.InfoListItem
            label={t('LiveStreamRecordingListItem.end')}
          >
            {recording.end
              ? DateTime.fromISO(recording.end).toLocaleString(
                  DateTime.DATETIME_FULL,
                  { locale: i18n.language },
                )
              : t('LiveStreamRecordingListItem.streaming')}
          </ContentBlock.InfoListItem>

          {recording.duration && (
            <ContentBlock.InfoListItem label={t('common.duration')}>
              <Duration ms={recording.duration} />
            </ContentBlock.InfoListItem>
          )}

          {playbackUrl && (
            <ContentBlock.InfoListItem
              label={t('LiveStreamRecordingListItem.playbackUrl')}
            >
              <CopyableText text={playbackUrl}>
                <Text truncate>
                  {t('LiveStreamRecordingListItem.copyPlaybackUrl')}
                </Text>
              </CopyableText>
            </ContentBlock.InfoListItem>
          )}

          {recording.conversion?.status === 'COMPLETE' && downloadUrl && (
            <ContentBlock.InfoListItem
              label={t('LiveStreamRecordingListItem.downloadUrl')}
            >
              <CopyableText text={downloadUrl}>
                <Text truncate>
                  {t('LiveStreamRecordingListItem.copyDownloadUrl')}
                </Text>
              </CopyableText>
            </ContentBlock.InfoListItem>
          )}

          {iframeCode && (
            <ContentBlock.InfoListItem
              label={t('LiveStreamRecordingListItem.iframe.label')}
            >
              <CopyableText text={iframeCode}>
                {t('LiveStreamRecordingListItem.iframe.value')}
              </CopyableText>
            </ContentBlock.InfoListItem>
          )}
        </ContentBlock.InfoList>
      </Stack>

      {recording.state === 'ended' && (
        <Stack horizontal gap={2}>
          {!recording.conversion ? (
            <Button
              icon={<FontAwesomeIcon icon={faCloudArrowDown} />}
              onClick={handleRequestDownload}
              variant="outlined"
            >
              {t('LiveStreamRecordingListItem.requestDownload')}
            </Button>
          ) : recording.conversion?.status === 'SUBMITTED' ? (
            <Button
              icon={<FontAwesomeIcon icon={faCloudArrowDown} />}
              variant="outlined"
              loading
            >
              {t('LiveStreamRecordingListItem.requestDownloadStatus.submitted')}
            </Button>
          ) : recording.conversion?.status === 'PROGRESSING' ? (
            <Button
              icon={
                <CircularProgressIndicator
                  value={
                    prepareDownloadProgress !== undefined &&
                    prepareDownloadProgress > 0
                      ? prepareDownloadProgress
                      : undefined
                  }
                />
              }
              variant="outlined"
              disabled
            >
              {t(
                'LiveStreamRecordingListItem.requestDownloadStatus.progressing',
                { progress: prepareDownloadFormattedProgress },
              )}
            </Button>
          ) : recording.conversion?.status === 'COMPLETE' ? (
            downloadUrl && (
              <Button
                icon={<FontAwesomeIcon icon={faCloudArrowDown} />}
                href={downloadUrl}
                target="_blank"
              >
                {t('common.download')}
              </Button>
            )
          ) : null}
        </Stack>
      )}
    </Stack>
  );
};
